// src/components/PaymentSuccess.js

import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Box,
  Card,
  CardContent
} from '@mui/material';
import { useParams } from 'react-router-dom';
import api from '../api';

export default function PaymentSuccess() {
  const { id } = useParams();
  const [order, setOrder]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    async function fetchOrder() {
      try {
        const email = localStorage.getItem('userEmail') || '';
        const { data } = await api.get(`/orders/${id}`, { params: { userEmail: email } });
        if (mounted) setOrder(data);
      } catch (err) {
        console.error(err);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchOrder();
    return () => { mounted = false; };
  }, [id]);

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!order) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Payment Successful
        </Typography>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Order not found.
        </Typography>

        {/* Pickup details are still shown for the guest’s convenience */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Pickup details</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography><strong>Location:</strong> Capitol Cafe</Typography>
            <Typography><strong>Address:</strong> 1 Capitol Sq, Columbus, OH 43215</Typography>
            <Typography><strong>Pickup window:</strong> 8 AM to 3 PM</Typography>
            <Typography><strong>Food prep time:</strong> 10 minutes</Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Payment Successful
      </Typography>

      <Typography>ID: {order.id}</Typography>
      <Typography>Date: {new Date(order.createdAt).toLocaleString()}</Typography>
      <Typography>Type: {order.orderType}</Typography>

      {/* New pickup details block */}
      <Card sx={{ my: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Pickup details</Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography><strong>Location:</strong> Capitol Cafe</Typography>
          <Typography><strong>Address:</strong> 1 Capitol Sq, Columbus, OH 43215</Typography>
          <Typography><strong>Pickup window:</strong> 8 AM to 3 PM</Typography>
          <Typography><strong>Food prep time:</strong> 10 minutes</Typography>
        </CardContent>
      </Card>

      <List sx={{ mt: 2 }}>
        {order.items.map((i, idx) => {
          const upcharge = i.glutenFreeOption ? 1 : 0;
          const unitPrice = i.price + upcharge;
          const lineTotal = unitPrice * i.quantity;

          return (
            <Box key={idx} sx={{ mb: 2 }}>
              <ListItem disableGutters>
                <ListItemText
                  primary={`${i.name}${upcharge ? ' (Gluten Free)' : ''} × ${i.quantity}`}
                  secondary={`$${unitPrice.toFixed(2)} each → $${lineTotal.toFixed(2)}`}
                />
              </ListItem>

              {i.note && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ pl: 2, mb: 1 }}
                >
                  Note: {i.note}
                </Typography>
              )}

              {upcharge ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ pl: 2, mb: 1 }}
                >
                  (+$1.00 gluten free upcharge per unit)
                </Typography>
              ) : null}

              <Divider />
            </Box>
          );
        })}
      </List>

      <Box sx={{ mt: 2 }}>
        <Typography>Subtotal: ${Number(order.subtotal).toFixed(2)}</Typography>
        <Typography>Tax: ${Number(order.tax).toFixed(2)}</Typography>
        <Typography variant="h6" sx={{ mt: 1 }}>
          Total: ${Number(order.total).toFixed(2)}
        </Typography>
      </Box>
    </Container>
  );
}
